import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import authStore from '../../store/authStore';
import QRScanner from '../../components/shared/QRScanner';
import {
  LogOut, Bus, MapPin, Clock, Users, CheckCircle2, Plus, Minus,
  XCircle, Camera, ChevronRight, User, Phone, AlertTriangle
} from 'lucide-react';
import './DashboardAutista.css';

/**
 * Contapersone della corsa.
 *
 * Somma chi ha scansionato la tessera e chi è salito senza: i due numeri
 * restano distinti, così a fine giro si capisce quanti hanno dimenticato
 * il tesserino.
 */
const Contapersone = ({ conTessera, senzaTessera, onCambia, disabilitato }) => (
  <div className="da-contapersone">
    <div className="da-contapersone-totale">
      <strong>{conTessera + senzaTessera}</strong>
      <span>passeggeri a bordo</span>
    </div>
    <div className="da-contapersone-dettaglio">
      <span>{conTessera} con tessera</span>
      <div className="da-contapersone-manuale">
        <button
          type="button"
          onClick={() => onCambia(-1)}
          disabled={disabilitato || senzaTessera === 0}
          aria-label="Togli un passeggero senza tessera"
        >
          <Minus size={18} />
        </button>
        <span><strong>{senzaTessera}</strong> senza tessera</span>
        <button
          type="button"
          onClick={() => onCambia(1)}
          disabled={disabilitato}
          aria-label="Aggiungi un passeggero senza tessera"
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  </div>
);

const MOTIVI = {
  ANAGRAFICA_INCOMPLETA: 'anagrafica incompleta',
  GRUPPO_NON_CORRISPONDENTE: 'gruppo non corrispondente',
  ABBONAMENTO_NON_ANCORA_VALIDO: 'non ancora valido',
  MENSILITA_NON_SALDATA: 'mensilità non saldata',
  ABBONAMENTO_SCADUTO: 'abbonamento scaduto',
};

const DashboardAutista = () => {
  const navigate = useNavigate();
  const { utente, logout } = authStore();
  const [lineaSelezionata, setLineaSelezionata] = useState(null);
  const [corsaAttiva, setCorsaAttiva] = useState(null);
  const [linee, setLinee] = useState([]);
  const [passeggeri, setPasseggeri] = useState([]);
  const [pullman, setPullman] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showPullmanModal, setShowPullmanModal] = useState(false);
  const [showTipoModal, setShowTipoModal] = useState(false);
  const [pullmanSelezionato, setPullmanSelezionato] = useState(null);
  const [tipoCorsa, setTipoCorsa] = useState('ANDATA');
  const [ultimaScansione, setUltimaScansione] = useState(null);
  const [loading, setLoading] = useState(true);
  const [senzaTessera, setSenzaTessera] = useState(0);
  const [inAggiornamento, setInAggiornamento] = useState(false);
  const lastScanTokenRef = React.useRef(null);
  const scanInProgressRef = React.useRef(false);

  useEffect(() => {
    if (utente?.id) {
      caricaLinee();
      caricaPullman();
      recuperaCorsaAperta();
    }
  }, [utente]);

  useEffect(() => {
    if (corsaAttiva) {
      caricaPasseggeriCorsa();
    }
  }, [corsaAttiva]);

  // Se l'autista ha già una corsa aperta la si riprende: rientrando
  // nell'app si ripartiva da zero e la corsa precedente restava aperta.
  const recuperaCorsaAperta = async () => {
    try {
      const res = await api.get('/trasporti/corse/corsa_attiva/');
      const corsa = res.data?.corsa;
      if (corsa) {
        setCorsaAttiva(corsa);
        setSenzaTessera(corsa.passeggeri_manuali || 0);
        setLineaSelezionata({
          id: corsa.linea,
          codice: corsa.linea_codice,
          nome: corsa.linea_nome,
        });
        setTipoCorsa(corsa.tipo_corsa);
      }
    } catch (error) {
      console.error('Errore recupero corsa in corso:', error);
    }
  };

  const caricaLinee = async () => {
    if (!utente?.id) return;
    try {
      setLoading(true);
      const response = await api.get(`/autenticazione/utenti/${utente.id}/`);
      setLinee(response.data.linee_assegnate || []);
    } catch (error) {
      console.error('Errore caricamento linee:', error);
    } finally {
      setLoading(false);
    }
  };

  const caricaPullman = async () => {
    try {
      const response = await api.get('/trasporti/mezzi/', {
        params: { attivo: true }
      });
      setPullman(response.data.results || response.data || []);
    } catch (error) {
      console.error('Errore caricamento pullman:', error);
    }
  };

  // L'elenco lo calcola il server in un colpo solo. Prima si facevano
  // due chiamate paginate: arrivavano 20 abbonamenti su 362 e 20
  // scansioni su 76, quindi chi restava fuori non risultava mai salito
  // e il contatore lo ignorava.
  const caricaPasseggeriCorsa = async () => {
    if (!corsaAttiva) return;
    try {
      const res = await api.get(`/trasporti/corse/${corsaAttiva.id}/elenco_passeggeri/`);
      setPasseggeri(res.data.passeggeri || []);
      setSenzaTessera(res.data.senza_tessera || 0);
    } catch (error) {
      console.error('Errore caricamento passeggeri:', error);
    }
  };

  // Dopo una scansione si aggiorna la sola riga interessata: ricaricare
  // 362 passeggeri a ogni tesserino renderebbe la salita lentissima.
  const aggiornaPasseggero = (abbonamentoId, valido, motivo) => {
    if (!abbonamentoId) return;
    const adesso = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    setPasseggeri((elenco) => elenco.map((p) => {
      if (p.abbonamento_id !== abbonamentoId) return p;
      if (valido) return { ...p, salito: true, orario: adesso, motivo_rifiuto: null };
      return p.salito ? p : { ...p, motivo_rifiuto: motivo };
    }));
  };

  // Chi sale senza tessera: si salva subito sulla corsa, così il numero
  // resta anche se l'app viene chiusa a metà giro.
  const cambiaSenzaTessera = async (delta) => {
    if (!corsaAttiva || inAggiornamento) return;
    const nuovo = Math.max(0, senzaTessera + delta);
    if (nuovo === senzaTessera) return;
    setSenzaTessera(nuovo);
    setInAggiornamento(true);
    try {
      await api.patch(`/trasporti/corse/${corsaAttiva.id}/`, { passeggeri_manuali: nuovo });
    } catch (error) {
      console.error('Errore aggiornamento contapersone:', error);
      setSenzaTessera(senzaTessera);
    } finally {
      setInAggiornamento(false);
    }
  };

  const handleScan = async (token) => {
    if (!token || !corsaAttiva) return;
    if (lastScanTokenRef.current === token || scanInProgressRef.current) return;

    scanInProgressRef.current = true;
    lastScanTokenRef.current = token;

    try {
      const response = await api.post('/abbonamenti/abbonamenti/valida_qr/', {
        token: token,
        corsa_id: corsaAttiva.id
      });
      setUltimaScansione({
        timestamp: new Date(),
        valido: response.data.valido,
        motivo: response.data.motivo,
        messaggio: response.data.messaggio,
        avviso: response.data.avviso,
        // Le altre salite di oggi: se ce ne sono, il tesserino e' gia'
        // a bordo di un altro mezzo e questo e' un secondo ragazzo.
        giaSalito: response.data.gia_salito || [],
        abbonamento: response.data.abbonamento,
        cliente: response.data.beneficiario
      });
      // Si aggiorna la riga del passeggero: salito se valido, con il
      // motivo bene in vista se respinto.
      aggiornaPasseggero(
        response.data.abbonamento?.id,
        response.data.valido,
        response.data.motivo,
      );
      setTimeout(() => { lastScanTokenRef.current = null; }, 3000);
    } catch (error) {
      console.error('Errore validazione QR:', error);
      setUltimaScansione({
        timestamp: new Date(),
        valido: false,
        messaggio: error.response?.data?.messaggio || 'Errore di connessione',
        abbonamento: null
      });
      setTimeout(() => { lastScanTokenRef.current = null; }, 3000);
    } finally {
      scanInProgressRef.current = false;
    }
  };

  const handleQRScan = (decodedText) => { handleScan(decodedText); };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSelezionaLinea = (linea) => {
    setLineaSelezionata(linea);
    setShowTipoModal(true);
  };

  const handleConfermaTipo = () => {
    setShowTipoModal(false);
    setShowPullmanModal(true);
  };

  const handleCreaEInizia = async () => {
    if (!pullmanSelezionato || !lineaSelezionata) {
      alert('Seleziona un pullman per continuare');
      return;
    }
    try {
      const oggi = new Date().toISOString().split('T')[0];
      const ora = new Date().toTimeString().split(' ')[0];
      const response = await api.post('/trasporti/corse/', {
        linea: lineaSelezionata.id,
        autista: utente.id,
        mezzo: pullmanSelezionato,
        data: oggi,
        tipo_corsa: tipoCorsa,
        orario_partenza_effettivo: ora,
        stato: 'IN_CORSO'
      });
      setCorsaAttiva(response.data);
      setSenzaTessera(response.data.passeggeri_manuali || 0);
      setShowPullmanModal(false);
      setPullmanSelezionato(null);
    } catch (error) {
      console.error('Errore creazione corsa:', error);
      const bloccata = error.response?.data?.corsa_aperta;
      if (bloccata) {
        // Una corsa è già aperta: la si riprende invece di crearne un'altra
        alert(Array.isArray(bloccata) ? bloccata[0] : bloccata);
        setShowPullmanModal(false);
        setPullmanSelezionato(null);
        await recuperaCorsaAperta();
        return;
      }
      alert('Errore durante la creazione della corsa: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleTerminaCorsa = async () => {
    if (!corsaAttiva) return;
    if (!window.confirm('Sei sicuro di voler terminare questa corsa?')) return;
    try {
      await api.patch(`/trasporti/corse/${corsaAttiva.id}/`, {
        stato: 'COMPLETATA',
        numero_passeggeri: passeggeri.filter(p => p.salito).length + senzaTessera
      });
      setCorsaAttiva(null);
      setLineaSelezionata(null);
      setPasseggeri([]);
    } catch (error) {
      console.error('Errore termine corsa:', error);
    }
  };

  // === LOADING ===
  if (loading) {
    return (
      <div className="da-root">
        <div className="da-loading">
          <div className="da-spinner"></div>
          <p>Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="da-root">
      {/* HEADER */}
      <header className="da-header">
        <div className="da-header-inner">
          <div className="da-header-left">
            <div className="da-header-icon"><Bus size={20} /></div>
            <div className="da-header-text">
              <h1>Dashboard Autista</h1>
              <p>{utente?.first_name || utente?.username}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="da-logout" title="Esci">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* MAIN */}
      <div className="da-main">
        {!corsaAttiva ? (
          /* === SELEZIONE LINEA === */
          <div>
            <h2 className="da-select-title">Seleziona la linea</h2>
            <p className="da-select-date">
              {new Date().toLocaleDateString('it-IT', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
              })}
            </p>

            {linee.length === 0 ? (
              <div className="da-empty">
                <Bus size={48} />
                <h3>Nessuna linea assegnata</h3>
                <p>Contatta l'amministratore per l'assegnazione</p>
              </div>
            ) : (
              <div className="da-lines">
                {linee.map((linea) => (
                  <button key={linea.id} className="da-line-card" onClick={() => handleSelezionaLinea(linea)}>
                    <div className="da-line-icon"><Bus size={22} /></div>
                    <div className="da-line-body">
                      <p className="da-line-name">{linea.nome}</p>
                      <div className="da-line-meta">
                        <span className="da-line-meta-item">
                          <Clock size={13} />
                          {linea.orario_partenza || '--:--'} – {linea.orario_arrivo || '--:--'}
                        </span>
                        <span className="da-line-meta-item">
                          <MapPin size={13} />
                          {linea.giorni_servizio}
                        </span>
                      </div>
                    </div>
                    <span className="da-line-badge">{linea.codice}</span>
                    <ChevronRight size={18} className="da-line-arrow" />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* === CORSA ATTIVA === */
          <div className="da-active">
            <div className="da-active-card">
              <div className="da-active-top">
                <button
                  className="da-back"
                  onClick={() => {
                    setCorsaAttiva(null);
                    setLineaSelezionata(null);
                    setPasseggeri([]);
                    setShowScanner(false);
                  }}
                >
                  ← Cambia Linea
                </button>

                <div className="da-active-name">
                  <Bus size={22} />
                  <h2>{lineaSelezionata?.nome}</h2>
                </div>

                <span className="da-status-live">
                  <span className="da-status-dot"></span>
                  IN CORSO
                </span>

                <div className="da-active-details">
                  <div className="da-detail-row">
                    <MapPin size={16} />
                    <span>{tipoCorsa}</span>
                  </div>
                  {corsaAttiva?.mezzo_targa && (
                    <div className="da-detail-row">
                      <Bus size={16} />
                      <span>Pullman: {corsaAttiva.mezzo_targa}</span>
                    </div>
                  )}
                  <div className="da-detail-row">
                    <Users size={16} />
                    <span>{passeggeri.filter(p => p.salito).length} / {passeggeri.length} con tessera</span>
                  </div>
                </div>
              </div>

              <Contapersone
                conTessera={passeggeri.filter(p => p.salito).length}
                senzaTessera={senzaTessera}
                onCambia={cambiaSenzaTessera}
                disabilitato={inAggiornamento}
              />

              <div className="da-actions">
                <button className="da-btn-scan" onClick={() => setShowScanner(true)}>
                  <Camera size={18} />
                  Scansiona QR
                </button>
                <button className="da-btn-end" onClick={handleTerminaCorsa}>
                  Termina Corsa
                </button>
              </div>
            </div>

            {/* PASSEGGERI */}
            <div className="da-pax-card">
              <h3 className="da-pax-title">
                <Users size={18} />
                Passeggeri ({passeggeri.filter(p => p.salito).length}/{passeggeri.length})
              </h3>

              {passeggeri.length === 0 ? (
                <div className="da-pax-empty">
                  <AlertTriangle size={40} />
                  <p>Nessun passeggero con abbonamento attivo per questa linea</p>
                </div>
              ) : (
                <div className="da-pax-list">
                  {passeggeri.map((p) => (
                    <div key={p.abbonamento_id} className={`da-pax-item ${p.salito ? 'boarded' : ''}`}>
                      {p.foto ? (
                        <img src={p.foto} alt={p.nome} className="da-pax-foto" />
                      ) : (
                        <div className="da-pax-foto-ph"><User size={18} /></div>
                      )}
                      <div className="da-pax-info">
                        <span className="da-pax-name">{p.nome}</span>
                        <span className="da-pax-code">
                          {p.codice}
                          {p.localita ? ` · ${p.localita}` : ''}
                        </span>
                        {/* Scansione respinta: l'autista deve sapere perché,
                            altrimenti il passeggero sparisce senza spiegazione */}
                        {!p.salito && p.motivo_rifiuto && (
                          <span className="da-pax-rifiuto">
                            {MOTIVI[p.motivo_rifiuto] || p.motivo_rifiuto}
                          </span>
                        )}
                      </div>
                      {p.salito ? (
                        <div className="da-pax-salito">
                          <CheckCircle2 size={20} className="da-pax-check" />
                          {p.orario && <span>{p.orario}</span>}
                        </div>
                      ) : (
                        <div className="da-pax-pending"></div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* === QR SCANNER === */}
      {showScanner && (
        <>
          <QRScanner
            onScan={handleQRScan}
            onClose={() => { setShowScanner(false); setUltimaScansione(null); }}
            title="Scansiona QR Abbonamento"
            subtitle="Inquadra il codice QR dello studente"
          />

          {/* Il contatore resta davanti allo scanner: è lì che serve */}
          <div className="da-contatore-scanner">
            <div className="da-contatore-cifra">
              <strong>{passeggeri.filter(p => p.salito).length + senzaTessera}</strong>
              <span>a bordo</span>
            </div>
            <div className="da-contatore-azioni">
              <button
                type="button"
                onClick={() => cambiaSenzaTessera(-1)}
                disabled={inAggiornamento || senzaTessera === 0}
                aria-label="Togli un passeggero senza tessera"
              >
                <Minus size={22} />
              </button>
              <span className="da-contatore-manuale">
                {senzaTessera}
                <small>senza tessera</small>
              </span>
              <button
                type="button"
                onClick={() => cambiaSenzaTessera(1)}
                disabled={inAggiornamento}
                aria-label="Aggiungi un passeggero senza tessera"
              >
                <Plus size={22} />
              </button>
            </div>
          </div>

          {/* Risultato scansione sopra lo scanner */}
          {ultimaScansione && (
            <div className="da-scan-overlay">
              <div className={`da-scan-result ${ultimaScansione.valido ? 'valid' : 'invalid'}`}>
                <div className={`da-scan-result-header ${ultimaScansione.valido ? 'valid' : 'invalid'}`}>
                  {ultimaScansione.valido ? <CheckCircle2 size={24} /> : <XCircle size={24} />}
                  <span>{ultimaScansione.valido ? 'ABBONAMENTO VALIDO' : 'NON VALIDO'}</span>
                </div>

                {ultimaScansione.cliente && (
                  <div className="da-scan-client">
                    <div className="da-scan-client-row">
                      {ultimaScansione.cliente.foto ? (
                        <img src={ultimaScansione.cliente.foto} alt={ultimaScansione.cliente.nome_completo} className="da-scan-client-foto" />
                      ) : (
                        <div className="da-scan-client-foto-ph"><User size={28} /></div>
                      )}
                      <div className="da-scan-client-info">
                        <h4>{ultimaScansione.cliente.nome_completo}</h4>
                        {ultimaScansione.cliente.tutore_nome_completo && (
                          <div className="da-scan-client-detail">
                            <User size={13} />
                            <span>{ultimaScansione.cliente.tutore_nome_completo}</span>
                          </div>
                        )}
                        {ultimaScansione.cliente.telefono && (
                          <div className="da-scan-client-detail">
                            <Phone size={13} />
                            <span>{ultimaScansione.cliente.telefono}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {ultimaScansione.abbonamento && (
                      <div className="da-scan-abb-code">
                        <p className="label">Codice Abbonamento</p>
                        <p className="code">{ultimaScansione.abbonamento.codice}</p>
                        {ultimaScansione.abbonamento.tratta && (
                          <p className="label" style={{ marginTop: 6 }}>
                            {ultimaScansione.abbonamento.tratta}
                            {ultimaScansione.abbonamento.direzione_display
                              ? ` · ${ultimaScansione.abbonamento.direzione_display}`
                              : ''}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Gruppo sbagliato: il cliente non può salire su questo mezzo */}
                {ultimaScansione.motivo === 'GRUPPO_NON_CORRISPONDENTE' && (
                  <div className="da-scan-msg" style={{ background: '#fef2f2', border: '2px solid #dc2626' }}>
                    <p style={{ color: '#991b1b', fontWeight: 700 }}>
                      ⛔ ABBONAMENTO DI UN ALTRO GRUPPO
                    </p>
                    <p style={{ color: '#991b1b' }}>{ultimaScansione.messaggio}</p>
                  </div>
                )}

                {/* Il riquadro del tesserino prestato dice gia' tutto, e
                    meglio: qui il messaggio sarebbe la stessa frase due volte. */}
                {ultimaScansione.motivo !== 'GRUPPO_NON_CORRISPONDENTE'
                  && ultimaScansione.messaggio
                  && !ultimaScansione.giaSalito?.length && (
                  <div className="da-scan-msg">
                    <p>{ultimaScansione.messaggio}</p>
                  </div>
                )}

                {/* Tesserino gia' a bordo di un altro mezzo: non si
                    blocca, ma si mette davanti agli occhi. Qui l'autista
                    ha la foto sopra e il ragazzo davanti: e' l'unico
                    momento in cui la cosa si puo' chiarire. */}
                {ultimaScansione.giaSalito?.length > 0 && (
                  <div className="da-scan-msg" style={{ background: '#fff7ed', border: '2px solid #ea580c' }}>
                    <p style={{ color: '#9a3412', fontWeight: 700 }}>
                      🔁 TESSERINO GIÀ SALITO OGGI
                    </p>
                    {ultimaScansione.giaSalito.map((s, i) => (
                      <p key={i} style={{ color: '#9a3412', marginTop: 4 }}>
                        {s.ora}
                        {s.mezzo ? ` · ${s.mezzo}` : ''}
                        {s.autista ? ` · ${s.autista}` : ''}
                      </p>
                    ))}
                    <p style={{ color: '#9a3412', marginTop: 6, fontSize: 13 }}>
                      Controlla la foto qui sopra e chiedi il nome: può essere
                      prestato. Se è tutto regolare fallo salire, l'ufficio
                      vede comunque la segnalazione. Se stai raccogliendo
                      ragazzi passati da un altro mezzo, è normale che compaia.
                    </p>
                  </div>
                )}

                {/* Direzione incoerente: si segnala, decide l'autista */}
                {ultimaScansione.valido && ultimaScansione.avviso && (
                  <div className="da-scan-msg" style={{ background: '#fffbeb', border: '2px solid #f59e0b' }}>
                    <p style={{ color: '#92400e', fontWeight: 600 }}>⚠️ {ultimaScansione.avviso}</p>
                  </div>
                )}

                <div className="da-scan-footer">
                  <button className="da-scan-continue" onClick={() => setUltimaScansione(null)}>
                    <Camera size={18} />
                    Continua a Scansionare
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* === MODAL TIPO CORSA === */}
      {showTipoModal && (
        <div className="da-modal-overlay">
          <div className="da-modal">
            <div className="da-modal-header">
              <h2><MapPin size={24} /> Tipo di Corsa</h2>
              <p>Seleziona andata o ritorno</p>
            </div>
            <div className="da-modal-body">
              <div className="da-tipo-options">
                <button className={`da-tipo-option ${tipoCorsa === 'ANDATA' ? 'selected' : ''}`} onClick={() => setTipoCorsa('ANDATA')}>
                  <div className="da-tipo-icon">→</div>
                  <div>
                    <p className="da-tipo-label">ANDATA</p>
                    <p className="da-tipo-desc">Casa → Scuola</p>
                  </div>
                </button>
                <button className={`da-tipo-option ${tipoCorsa === 'RITORNO' ? 'selected' : ''}`} onClick={() => setTipoCorsa('RITORNO')}>
                  <div className="da-tipo-icon">←</div>
                  <div>
                    <p className="da-tipo-label">RITORNO</p>
                    <p className="da-tipo-desc">Scuola → Casa</p>
                  </div>
                </button>
              </div>
            </div>
            <div className="da-modal-footer">
              <button className="da-modal-btn-cancel" onClick={() => { setShowTipoModal(false); setLineaSelezionata(null); }}>
                Annulla
              </button>
              <button className="da-modal-btn-confirm" onClick={handleConfermaTipo}>
                Continua
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === MODAL PULLMAN === */}
      {showPullmanModal && (
        <div className="da-modal-overlay">
          <div className="da-modal da-modal-lg">
            <div className="da-modal-header">
              <h2><Bus size={24} /> Seleziona il Pullman</h2>
              <p>Scegli il pullman per questa corsa</p>
            </div>
            <div className="da-modal-body-scroll">
              {pullman.length === 0 ? (
                <div className="da-pullman-empty">
                  <Bus size={48} />
                  <p>Nessun pullman disponibile</p>
                </div>
              ) : (
                <div className="da-pullman-options">
                  {pullman.map((mezzo) => (
                    <button
                      key={mezzo.id}
                      className={`da-pullman-option ${pullmanSelezionato === mezzo.id ? 'selected' : ''}`}
                      onClick={() => setPullmanSelezionato(mezzo.id)}
                    >
                      <div className="da-pullman-icon"><Bus size={22} /></div>
                      <div className="da-pullman-info">
                        <p className="da-pullman-targa">{mezzo.targa}</p>
                        <p className="da-pullman-modello">{mezzo.modello}</p>
                        {mezzo.numero_interno && <p className="da-pullman-num">N. {mezzo.numero_interno}</p>}
                      </div>
                      <div className="da-pullman-cap">
                        <span className="num">{mezzo.capienza}</span>
                        <span className="label">posti</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="da-modal-footer">
              <button className="da-modal-btn-cancel" onClick={() => { setShowPullmanModal(false); setPullmanSelezionato(null); }}>
                Annulla
              </button>
              <button className="da-modal-btn-confirm" disabled={!pullmanSelezionato} onClick={handleCreaEInizia}>
                Inizia Corsa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardAutista;
